import {
  ArchiveIcon,
  FolderInputIcon,
  Loader2Icon,
  ShieldCheckIcon,
  SquareStackIcon,
  UserRoundCheckIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import { use, useState, type ReactNode } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/primitives/alert-dialog.tsx"
import { Button } from "@/primitives/button.tsx"
import { DropdownMenuItem } from "@/primitives/dropdown-menu.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { grantWordingOf, type Grant } from "./confirmation.ts"
import { titleParts, type Wording } from "./destructive-confirmation.ts"
import { Kbd } from "./key.tsx"
import { MenuConfirmationContext } from "./menu-confirmation.ts"
import { useTriggerTooltip } from "./use-trigger-tooltip.ts"

/*
 * The dialog every confirmation asks in, whether it takes something away (DestructiveConfirmation,
 * red) or grants something consequential (Confirmation, the brand's teal): a tile with the case's
 * icon, the question, what follows from it, and Cancel beside the confirming button, which has
 * focus as the dialog opens so Enter confirms.
 *
 * Cancelling changes nothing. Confirming calls `onConfirm` and keeps the dialog open until it
 * settles: it closes once the action succeeds, and stays open when it fails, so the page can say
 * why (it toasts) and the Member can try again or cancel.
 */

/**
 * How the confirmation is reached: a labelled button, an item in a menu, or a row's muted ×; or
 * `none`, when something else of the page's asks it (unchecking an assignee in a Picker), opening
 * it through `open` and `onOpenChange`.
 */
export type ConfirmationTrigger = "button" | "menu-item" | "row-x" | "none"

type Tone = "destructive" | "brand"

const tones = {
  destructive: { button: "destructive-ghost", menu: "destructive", confirm: "destructive" },
  brand: { button: "outline", menu: "default", confirm: "default" },
} as const

/** Beyond this many characters a name in a title is shortened, and shown whole in a tooltip. */
const longSubject = 28

function Title({ wording }: { wording: Wording }) {
  const { before, subject, after } = titleParts(wording)
  if (!subject || subject.length <= longSubject) return <>{wording.title}</>
  return (
    <>
      {before}
      <Tooltip>
        <TooltipTrigger render={<span className="inline-block max-w-56 truncate align-bottom" />}>
          {subject}
        </TooltipTrigger>
        <TooltipContent>{subject}</TooltipContent>
      </Tooltip>
      {after}
    </>
  )
}

export function ConfirmationShell({
  tone,
  icon: Icon,
  wording,
  trigger,
  onConfirm,
  choice,
  open: openProp,
  onOpenChange,
}: {
  tone: Tone
  icon: LucideIcon
  wording: Wording
  trigger: ConfirmationTrigger
  onConfirm: () => Promise<unknown>
  choice?: ReactNode
  /** Whether it is open, when the page opens it rather than its trigger. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  // In a RowActions menu the menu holds whether its dialog is open, and draws each part apart.
  const host = trigger === "menu-item" ? use(MenuConfirmationContext) : undefined
  const [ownOpen, setOwnOpen] = useState(false)
  const open = host?.part === "dialog" ? host.open : (openProp ?? ownOpen)
  const setOpen = (next: boolean) => {
    if (host?.part === "dialog") host.onOpenChange(next)
    else if (openProp === undefined) setOwnOpen(next)
    onOpenChange?.(next)
  }
  const [pending, setPending] = useState(false)
  const look = tones[tone]
  // The row ×'s tooltip stays shut while its dialog asks, and when the dialog hands focus back.
  const tip = useTriggerTooltip(open)

  if (host?.part === "item")
    return (
      <DropdownMenuItem variant={look.menu} onClick={host.ask}>
        <Icon aria-hidden />
        {wording.menu}
      </DropdownMenuItem>
    )

  async function confirm() {
    setPending(true)
    try {
      await onConfirm()
      setOpen(false)
    } catch {
      // The page reports the failure; the dialog stays open so nothing looks done that is not.
    } finally {
      setPending(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => !pending && setOpen(next)}>
      {trigger === "none" || host ? null : trigger === "menu-item" ? (
        // Outside a RowActions menu, the item keeps its menu open behind the dialog: closing the
        // menu would unmount the item, and this dialog with it, before the Member had answered.
        <DropdownMenuItem variant={look.menu} closeOnClick={false} onClick={() => setOpen(true)}>
          <Icon aria-hidden />
          {wording.menu}
        </DropdownMenuItem>
      ) : trigger === "row-x" ? (
        <Tooltip {...tip.tooltip}>
          <TooltipTrigger
            render={
              <AlertDialogTrigger
                render={
                  <Button variant="destructive-ghost" size="icon-xs" aria-label={wording.label} />
                }
              />
            }
            {...tip.trigger}
          >
            <XIcon aria-hidden />
          </TooltipTrigger>
          <TooltipContent>{wording.label}</TooltipContent>
        </Tooltip>
      ) : (
        <AlertDialogTrigger render={<Button variant={look.button} size="sm" />}>
          {wording.trigger}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogMedia tone={tone}>
            <Icon aria-hidden />
          </AlertDialogMedia>
          <AlertDialogTitle>
            <Title wording={wording} />
          </AlertDialogTitle>
          <AlertDialogDescription>{wording.consequence}</AlertDialogDescription>
        </AlertDialogHeader>
        {choice}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant={look.confirm}
            // Pending, it keeps its colour and the focus, and takes no second press.
            disabled={pending}
            focusableWhenDisabled
            onClick={() => void confirm()}
          >
            {pending && <Loader2Icon aria-hidden className="animate-spin" />}
            {wording.confirm}
            {!pending && <Kbd keys={["↵"]} />}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const grantIcons = {
  "admin-grant": ShieldCheckIcon,
  "archive-project": ArchiveIcon,
  "epic-area": SquareStackIcon,
  "move-ticket": FolderInputIcon,
  "take-over": UserRoundCheckIcon,
} satisfies Record<Grant["confirms"], LucideIcon>

/*
 * The confirmation for a consequential action that takes nothing away: making a Member an Admin,
 * archiving a Project, moving an Epic's tickets with it to another Area, or moving a ticket out of
 * a Personal Project. It asks as DestructiveConfirmation does, in the brand's colour rather than
 * red, and says what the action lets or stops others doing, with any `choice` it needs made first.
 * `trigger` is a labelled outline button or an item in a menu; there is no row × for something that
 * adds. With `none`, the page opens it through `open` and `onOpenChange`, as when a Picker's choice
 * asks first.
 */
export function Confirmation({
  trigger,
  onConfirm,
  open,
  onOpenChange,
  choice,
  ...grant
}: Grant & {
  trigger: Exclude<ConfirmationTrigger, "row-x">
  onConfirm: () => Promise<unknown>
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** A question the action can't go ahead without, such as which Project a ticket moves to. */
  choice?: ReactNode
}) {
  return (
    <ConfirmationShell
      tone="brand"
      icon={grantIcons[grant.confirms]}
      wording={grantWordingOf(grant)}
      trigger={trigger}
      onConfirm={onConfirm}
      choice={choice}
      {...(open !== undefined && { open })}
      {...(onOpenChange && { onOpenChange })}
    />
  )
}
